import React, { useContext, useState, useEffect, useRef } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { Global, css } from '@emotion/react';
import { ThemeProvider, ThemeContext } from './theme-provider';
import * as normalize from '../styles/normalize.css';
import * as global from '../styles/global.css';
import FadeTransition from './fade-transition'
import SEO from './seo';
import Header from './header';
import Footer from './footer';
import Button from './button';
import ArrowRight from '../images/svg/arrow-right.svg';

function SubscribeModal({ subscribeText, shouldShow }) {
  const [isOpen, setIsOpen] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');
  const formLoadTime = useRef(Date.now());

  const handleSubscribeSubmit = async (e) => {
    e.preventDefault();

    if (Date.now() - formLoadTime.current < 2000) {
      return;
    }

    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const enquiryUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    
    const submitData = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      website: formData.get('website'),
      enquiryUrl: enquiryUrl,
    };

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };
  
  useEffect(() => {
    const closedTimestamp = localStorage.getItem('subscribeModalClosed');
    const now = new Date().getTime();
    
    if (closedTimestamp && now - parseInt(closedTimestamp) < 24 * 60 * 60 * 1000) {
      return;
    }
    
    if (shouldShow) {
      setIsOpen(true);
    }
  }, [shouldShow]);
  
  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('subscribeModalClosed', new Date().getTime().toString());
  };
  
  if (!isOpen) return null;
  
  return (
    <div
      className="type--extra-extra-small"
      css={css`
        position: fixed;
        bottom: 5rem;
        left: 5rem;
        z-index: 1000;
        width: 39rem;
        background-color: white;
        border: 1px solid var(--black);
        padding: 1.5rem;
        opacity: 0;
        transform: translateY(40px);
        animation: fadeInUp 0.6s var(--curve) forwards;

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 900px) {
          left: 20px;
          bottom: 20px;
          max-width: calc(100% - 4rem);
        }
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        `}
      >
        <div
          css={css`
            width: 90%;
          `}
        >
          {subscribeText}
        </div>
        <button
          onClick={handleClose}
          css={css`
            background: none;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            margin: 0;
          `}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1L0.514719 9.48528" stroke="black"/>
            <path d="M0.514648 1L8.99993 9.48528" stroke="black"/>
          </svg>
        </button>
      </div>
      {formStatus === 'success' ? (
        <div className="font--medium" css={css`padding: 2rem 0;`}>Thank you for subscribing.</div>
      ) : (
      <form
        id="subscribe-modal-form"
        onSubmit={handleSubscribeSubmit}
      >
        <input
          type="text"
          name="website"
          css={css`
            display: none;
          `}
          tabIndex="-1"
          autoComplete="off"
        />
        <fieldset disabled={formStatus === 'submitting'} css={css`border:0;padding:0;margin:0;`}>
        <div
          css={css`
            display: flex;
            gap: 1rem;
            margin-bottom: 0.5rem;

            @media (max-width: 600px) {
              flex-direction: column;
            }
          `}
        >
          <input
            className="font--medium"
            css={css`
              width: 100%;
              background-color: transparent;
              padding: 1rem 0;
              border: 0;
              border-bottom: 1px solid var(--black);
              &:focus::placeholder {
                color: transparent;
              }
            `}
            type="text"
            name="firstName"
            placeholder="First Name"
            required
          />
          <input
            className="font--medium"
            css={css`
              width: 100%;
              background-color: transparent;
              padding: 1rem 0;
              border: 0;
              border-bottom: 1px solid var(--black);
              &:focus::placeholder {
                color: transparent;
              }
            `}
            type="text"
            name="lastName"
            placeholder="Last Name"
            required
          />
        </div>
        <input
          className="font--medium"
          css={css`
            width: 100%;
            background-color: transparent;
            padding: 1rem 0;
            border: 0;
            border-bottom: 1px solid var(--black);
            margin-bottom: 2rem;
            &:focus::placeholder {
              color: transparent;
            }
          `}
          type="email"
          name="email"
          placeholder="Email address"
          required
        />
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            gap: 1rem;

            @media (max-width: 600px) {
              flex-direction: column;
            }
          `}
        >
          <div
            css={css`
              flex: 1;
              display: flex;
              align-items: flex-start;
              gap: 1rem;
            `}
          >
            <input
              type="checkbox"
              id="modal-terms-checkbox"
              required
              defaultChecked
              css={css`
                flex: 0 0 1.4rem;
                margin-top: 0.3rem;
                appearance: none;
                height: 1.4rem;
                border: 1px solid var(--black);
                background-color: transparent;
                cursor: pointer;
                
                &:checked {
                  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6 6l12 12M6 18L18 6' stroke='black' stroke-width='2' fill='none'/%3E%3C/svg%3E");
                  background-position: center;
                  background-repeat: no-repeat;
                  background-size: 1.1rem;
                }
              `}
            />
            <label 
              htmlFor="modal-terms-checkbox" 
              className="font--medium type--extra-extra-extra-small"
              css={css`
                position: relative;
                top: 0.4rem;
                cursor: pointer;

                a {
                  &:hover {
                    text-decoration: none;
                  }
                }
              `}
            >
              I accept the Terms &amp; Conditions of the Time &amp; Place <Link to="/privacy-policy/">Privacy Policy</Link>.
            </label>
          </div>
          <div
            css={css`
              flex: 1;

              button {
                width: 100%;
              }
            `}
          >
            <Button 
              submit="subscribe-modal-form"
              text={formStatus === 'submitting' ? 'Signing...' : 'Sign Up'}
              arrow={<ArrowRight />}
            />
          </div>
        </div>
        </fieldset>
        {formStatus === 'error' && <div className="type--extra-extra-small" css={css`margin-top: 2rem;`}>There was an error submitting the form. Please try again.</div>}
      </form>
      )}
    </div>
  );
}

export default function Layout({ children, location }) {
  const theme = useContext(ThemeContext);
  const [scrolledHalfway, setScrolledHalfway] = useState(false);
  
  const data = useStaticQuery(graphql`
    query {
      footer: sanityFooter(_id: {eq: "footer"}) {
        subscribeText
      }
    }
  `);
  
  // Track scroll position to determine when to show modal
  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== 'undefined') {
        const scrollPosition = window.scrollY;
        const pageHeight = document.body.scrollHeight;
        const viewportHeight = window.innerHeight;
        
        // Check if scrolled halfway down the page
        if (scrollPosition > (pageHeight - viewportHeight) / 2) {
          setScrolledHalfway(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      css={css`
        margin: 0 auto;
      `}
      onWheel={(e) => {
        theme.setShowMenu((e.nativeEvent.wheelDelta > 0 && e.nativeEvent.view.scrollY > e.nativeEvent.view.window.innerHeight))

        if (window) {
          if (window.scrollY > window.innerHeight) {
            theme.setPostIntro(true);
          } else {
            theme.setPostIntro(false);
          }
        }
      }}
      onTouchMove={(e) => {
        setTimeout(() => {
          if (window) {
            if (window.scrollY > window.innerHeight) {
              theme.setPostIntro(true);
            } else {
              theme.setPostIntro(false);
            }
          }
        }, 2000);
      }}
    >
      <SEO />
      <Global
        styles={css`
          ${normalize}
          ${global}

          html, footer {
            background-color: ${(theme.backgroundColor) ? `${theme.backgroundColor} !important` : 'inherit'};
            transition: background-color 0.6s var(--curve);
          }
        `}
      />
      <Header />
      <FadeTransition location={location}>
        <main>
          {children}
        </main>
        <Footer />
      </FadeTransition>
      <SubscribeModal 
        subscribeText={data.footer?.subscribeText}
        shouldShow={scrolledHalfway}
      />
    </div>
  )
}
